import { GetFieldDecoratorOptions } from '@ant-design/compatible/lib/form/Form'
import { CatalogBuilderConfig } from './catalog/CatalogBuilder'
import { HtmlBuilderConfig } from './HtmlBuilder'
import { SolutionBuilderConfig } from './SolutionBuilder'
import { QuizBuilderConfig } from './QuizBuilder'
import { FunctionComponent, ReactNode } from 'react'
import { JsonObject } from 'type-fest'

export type FontAwesomeIconKey = `fa-${string}`
export type FontAwesomeIconClass = `fa ${FontAwesomeIconKey}`

/**
 * A BuilderConfig object contains all settings relevant to a specific builder.
 */
export type BuilderConfig<T> = T extends string
  ? {
      key: T
      builderHomeData: {
        type: T
        description: ReactNode
        enabled: boolean
        icon?: string
        route: string
        title: string
      }
      previewLabels: {
        title: string
        description: string
      }
      crumbTrail: {
        linkTo: '/builders/home'
        crumbText: string
        crumbIcon: FontAwesomeIconClass
      }
      fieldValidators: Record<string, GetFieldDecoratorOptions>
      formLabels: Record<string, string>
      icon: FontAwesomeIconKey
      /**
       * Screen handlers allow you to hook into the builder sider buttons (see button UI in
       * `BuilderSider.js` and handlers in `AssetBuilder.js`).  Each screen has its own
       * behavior, so the button handlers are specified by screen.  If a handler is provided,
       * it circumvents any behavior in `BuilderSider.js`.
       */
      screenHandlers: {
        /**
         * Create screen is often the same as the "meta" screen (see below), but is
         * called "createScreen" when the asset is being created.
         */
        createScreen?: {
          next?: (saveToDb: VoidFunction, data?: { data: string }) => void
        }
        metaScreen?: {
          /**
           * If defined, called after `setKeyForBuilder` and `saveTags`.
           */
          next?: (saveToDb: VoidFunction, isEdit: boolean) => void
        }
      }
      /**
       * The SAVE_DATA_FOR_BUILDER and UPDATE_BUILDER actions rely on this to serialize
       * asset data to be saved in S3.  All of the context available to the builder/editor
       * is provided.
       */
      assetSerializer: (data: {
        builderType: string
        builderData: any // TODO: typing here would be...very helpful
        allAssets: Record<string, any>
        user: any
        builderSettings: any
      }) => {
        assetMetaData: JsonObject
        // TODO: it would be good to support different content types (e.g.
        // TSV for comparisons), but the `updateBuilderSaga` code will need
        // to be updated to handle this, for now, this only supports JSON data.
        // see the `if (ContentType !== 'application/json')` statement in
        // src/dux/actions/builders.js for more details
        ContentType: 'application/json'
        assetData: JsonObject
        assetDataSerialized: string
      }
      /**
       * If provided, will override the content area in `AssetBuilderView`, allowing the config
       * to take full control of the main content area.
       */
      // TODO: endpoints typing
      Content?: FunctionComponent<{ endpoints: any }>
    }
  : never

// TODO: the typing here could use some work....
export const builderConfigs: Record<string, BuilderConfig<string>> = {
  [CatalogBuilderConfig.key]: CatalogBuilderConfig,
  [HtmlBuilderConfig.key]: HtmlBuilderConfig,
  [SolutionBuilderConfig.key]: SolutionBuilderConfig,
  [QuizBuilderConfig.key]: QuizBuilderConfig,
}
